/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

/**
 * URL helpers for query-string parsing, building, and history updates.
 */
export const urlHelper = {
  /**
   * Matches absolute URLs, including protocol-relative ones (`//cdn.example.com`).
   *
   * @readonly
   */
  ABS_URL_PATTERN: /^(?:[a-z]+:)?\/\//i,

  /**
   * Matches plain decimal numbers only (no leading zeros, hex, or scientific notation).
   * Keeps values like `"001"` / `"0x10"` as strings.
   *
   * @readonly
   */
  SAFE_NUMBER_PATTERN: /^-?(?:0|[1-9]\d*)(?:\.\d+)?$/,

  /**
   * Returns whether the given URL is absolute (including protocol-relative URLs).
   *
   * @example
   *
   * ```txt
   *   http://example.com         // true  - http absolute URL
   *   HTTP://EXAMPLE.COM         // true  - upper-case scheme
   *   https://www.example.com    // true  - https absolute URL
   *   ftp://example.com/file.txt // true  - ftp absolute URL
   *   //cdn.example.com/lib.js   // true  - protocol-relative URL
   *   /myfolder/test.txt         // false - root-relative URL
   *   test                       // false - relative path
   * ```
   *
   * @param {string} url URL to test
   *
   * @returns {boolean} `true` when absolute; otherwise `false`
   */
  isAbsolute(url) {
    return typeof url === 'string' && this.ABS_URL_PATTERN.test(url);
  },

  /**
   * Creates a `URL` instance resolved against the current location.
   * Prefer {@link urlHelper.splitUrl} / {@link urlHelper.composeUrl} when you need
   * to preserve a relative input URL without resolving it to an absolute href.
   *
   * @param {string} url
   *
   * @returns {URL}
   */
  createUrl(url) {
    return new URL(url == null ? '' : String(url), window.location.href);
  },

  /**
   * Builds a `URLSearchParams` from a query string, full/relative URL, or
   * existing `URLSearchParams` instance.
   *
   * @param {string|URLSearchParams} [input=location.search]
   *
   * @returns {URLSearchParams}
   */
  toSearchParams(input = location.search) {
    if (input instanceof URLSearchParams) {
      return input;
    }

    if (input == null || input === '') {
      return new URLSearchParams();
    }

    const value = String(input);

    // Absolute, protocol-relative, root-relative, or any value with query/hash:
    // let URL extract `.searchParams` (also drops `#fragment` safely).
    if (
      this.isAbsolute(value)
      || value.charAt(0) === '/'
      || value.includes('?')
      || value.includes('#')
    ) {
      try {
        return this.createUrl(value).searchParams;
      } catch (error) {
        // Fall through and treat the value as a bare query string.
      }
    }

    return new URLSearchParams(value.charAt(0) === '?' ? value.slice(1) : value);
  },

  /**
   * Splits a URL into base path, query string (including `?`), and hash.
   * Does not resolve relative URLs against the current location.
   *
   * @param {string} url
   *
   * @returns {{base: string, search: string, hash: string}}
   */
  splitUrl(url) {
    const href = url == null ? '' : String(url);
    const hashIndex = href.indexOf('#');
    const hash = hashIndex >= 0 ? href.slice(hashIndex) : '';
    const withoutHash = hashIndex >= 0 ? href.slice(0, hashIndex) : href;
    const queryIndex = withoutHash.indexOf('?');

    return {
      base: queryIndex >= 0 ? withoutHash.slice(0, queryIndex) : withoutHash,
      search: queryIndex >= 0 ? withoutHash.slice(queryIndex) : '',
      hash,
    };
  },

  /**
   * Builds a URL from a base path, params object, and optional hash.
   * Uses string composition so relative inputs stay relative.
   *
   * @param {string} base   Path or absolute URL without query/hash
   * @param {object} params Query params
   * @param {string} [hash] Hash fragment, with or without leading `#`
   *
   * @returns {string}
   */
  composeUrl(base, params, hash = '') {
    const query = this.objectToQuery(params);
    let hashPart = hash || '';

    if (hashPart && hashPart.charAt(0) !== '#') {
      hashPart = `#${hashPart}`;
    }

    if (!query) {
      return `${base}${hashPart}`;
    }

    return `${base}?${query}${hashPart}`;
  },

  /**
   * Decodes a query-string component. Treats `+` as space and never throws
   * on malformed percent-encoding.
   *
   * @param {string} value
   *
   * @returns {string}
   */
  decodeQueryComponent(value) {
    const normalized = String(value).replace(/\+/g, ' ');

    try {
      return decodeURIComponent(normalized);
    } catch (error) {
      return normalized;
    }
  },

  /**
   * Returns whether two URLs point to the same resource after normalizing
   * path, query param order/encoding, and hash.
   *
   * @param {string} left
   * @param {string} right
   *
   * @returns {boolean}
   */
  isSameUrl(left, right) {
    const normalize = (value) => {
      const url = this.createUrl(value);
      const params = this.queryToObject(url.search);
      const ordered = {};

      Object.keys(params)
        .sort()
        .forEach((name) => {
          ordered[name] = params[name];
        });

      return `${url.pathname}?${this.objectToQuery(ordered)}${url.hash}`;
    };

    return normalize(left) === normalize(right);
  },

  /**
   * Pushes a new entry onto the session history stack.
   *
   * @param {string} url
   * @param {any}    [data=null]
   * @param {string} [title=''] Ignored by modern browsers; kept for API compatibility
   * @param {object} [options]  Passed to {@link urlHelper.prepareUrl}
   */
  pushState(url, data = null, title = '', options = {}) {
    const newUrl = this.prepareUrl(url, options);

    if (!this.isSameUrl(window.location.href, newUrl)) {
      window.history.pushState(data, title || '', newUrl);
    }
  },

  /**
   * Replaces the most recent history entry with the given URL.
   *
   * @param {string} url
   * @param {any}    [data=null]
   * @param {string} [title=''] Ignored by modern browsers; kept for API compatibility
   * @param {object} [options]  Passed to {@link urlHelper.prepareUrl}
   */
  replaceState(url, data = null, title = '', options = {}) {
    const newUrl = this.prepareUrl(url, options);

    if (!this.isSameUrl(window.location.href, newUrl)) {
      window.history.replaceState(data, title || '', newUrl);
    }
  },

  /**
   * Parses a query string (or full URL) into a params object via `URLSearchParams`.
   * Duplicate keys become arrays. Hash fragments are ignored.
   *
   * @param {string|URLSearchParams} [queryString=location.search]
   *
   * @returns {object}
   */
  queryToObject(queryString = location.search) {
    const params = {};
    const searchParams = this.toSearchParams(queryString);

    searchParams.forEach((rawValue, name) => {
      const value = this.formatValue(rawValue);

      if (Object.hasOwn(params, name)) {
        const current = params[name];

        params[name] = Array.isArray(current) ? current.concat(value) : [current, value];
      } else {
        params[name] = value;
      }
    });

    return params;
  },

  /**
   * Serializes a params object to a query string (without leading `?`) via
   * `URLSearchParams`. Arrays become repeated keys. `undefined` values are
   * skipped; `null` becomes empty.
   *
   * @param {object} params
   *
   * @returns {string}
   */
  objectToQuery(params) {
    if (params == null || typeof params !== 'object') {
      return '';
    }

    const searchParams = new URLSearchParams();

    Object.keys(params).forEach((name) => {
      const value = params[name];

      if (typeof value === 'undefined') {
        return;
      }

      if (Array.isArray(value)) {
        value.forEach((item) => {
          if (typeof item === 'undefined') {
            return;
          }

          searchParams.append(name, item === null ? '' : String(item));
        });

        return;
      }

      searchParams.append(name, value === null ? '' : String(value));
    });

    return searchParams.toString();
  },

  /**
   * Returns query param names from a URL/query string or params object.
   *
   * @param {string|object|null|undefined} input
   *
   * @returns {string[]}
   */
  getQueryNames(input) {
    if (input == null) {
      return [];
    }

    if (typeof input === 'string' || input instanceof URLSearchParams) {
      return Object.keys(this.queryToObject(input));
    }

    if (typeof input !== 'object') {
      return [];
    }

    return Object.keys(input);
  },

  /**
   * Merges params into the given URL's query string.
   * Existing keys are overwritten. Preserves relative vs absolute form of `url`,
   * and keeps any hash fragment.
   *
   * @param {string}        url
   * @param {string|object} params Query string or params object
   *
   * @returns {string} URL with merged query params
   */
  appendParams(url, params) {
    if (url == null) {
      return url;
    }

    if (params == null || params === '') {
      return String(url);
    }

    const {base, search, hash} = this.splitUrl(url);
    const urlParams = this.queryToObject(search);
    const nextParams = typeof params === 'string' || params instanceof URLSearchParams
      ? this.queryToObject(params)
      : params;

    if (nextParams == null || typeof nextParams !== 'object') {
      return String(url);
    }

    return this.composeUrl(base, {...urlParams, ...nextParams}, hash);
  },

  /**
   * Builds a URL for history updates by merging selected query params from the
   * current location with the target URL.
   *
   * Merge priority (low → high): `location.search` → params on `url` →
   * `options.params` (explicit overrides, e.g. Variation `m`).
   *
   * When `options.include` is empty, only param names present on the target URL
   * or in `options.params` are kept (values may still come from the current
   * location when missing on target / overrides).
   *
   * @param {string} url
   * @param {object} [options]
   * @param {string[]} [options.include] Param names to keep
   * @param {string[]} [options.exclude] Param names to drop
   * @param {string[]} [options.order]   Preferred param order
   * @param {object} [options.params]    Explicit query overrides (highest priority)
   *
   * @returns {string} Prepared URL (relative inputs stay relative)
   */
  prepareUrl(url, options = {}) {
    let {
      include = [],
      exclude = [],
      order = [],
      params: overrideParams = {},
    } = options;

    const {base, search, hash} = this.splitUrl(url == null ? '' : url);
    const nextParams = this.queryToObject(search);
    const unorderedParams = {
      ...this.queryToObject(window.location.search),
      ...nextParams,
      ...overrideParams,
    };

    if (include.length === 0) {
      include = Object.keys({...nextParams, ...overrideParams});
    }

    const params = {};
    const seen = new Set();

    // Prefer `order`, then remaining keys, for a stable query string.
    order.concat(Object.keys(unorderedParams)).forEach((name) => {
      if (seen.has(name)) {
        return;
      }

      seen.add(name);

      if (Object.hasOwn(unorderedParams, name) && include.includes(name) && !exclude.includes(name)) {
        params[name] = unorderedParams[name];
      }
    });

    return this.composeUrl(base, params, hash);
  },

  /**
   * Coerces a query value to a richer type (similar to jQuery `.data()`).
   * Only plain decimal numeric strings are converted to numbers — values with
   * leading zeros, hex prefixes, or empty/whitespace stay as strings.
   *
   * @param {string} value Raw query value
   *
   * @returns {any} Coerced value
   */
  formatValue(value) {
    if (typeof value !== 'string') {
      return value;
    }

    if (value === 'true') {
      return true;
    }

    if (value === 'false') {
      return false;
    }

    if (value === 'null') {
      return null;
    }

    if (value === 'undefined') {
      return undefined;
    }

    if (this.SAFE_NUMBER_PATTERN.test(value)) {
      const number = Number(value);

      if (Number.isFinite(number)) {
        return number;
      }
    }

    return value;
  },
};
